import { useState } from 'react';

import { useAuth } from '../hooks/useAuth.jsx';

/**
 * Neon Auth email/password sign-in and sign-up.
 *
 * @param {{onAuthenticated?: () => void, compact?: boolean}} props
 */
export default function AuthForm({ onAuthenticated, compact = false }) {
  const { signIn, signUp, refresh, isConfigured } = useAuth();
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  async function handleSubmit(event) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);

    try {
      const action = mode === 'signin' ? signIn : signUp;
      const { data, error: authError } = await action(email.trim(), password);
      if (authError) throw new Error(authError.message);

      if (mode === 'signup' && !data?.session) {
        setNotice('Account created. Check your inbox to confirm your email, then sign in.');
        setMode('signin');
      } else {
        // Neon Auth keeps the session in an HTTP-only cookie, so the provider
        // has to re-read it to obtain the JWT the API calls need.
        await refresh();
        setNotice(mode === 'signin' ? 'Signed in.' : 'Account ready.');
        onAuthenticated?.();
      }
      setPassword('');
    } catch (submitError) {
      setError(submitError.message || 'Authentication failed');
    } finally {
      setBusy(false);
    }
  }

  if (!isConfigured) {
    return (
      <p className="rounded-lg border border-caution/40 bg-caution/10 px-4 py-3 text-sm text-caution">
        Neon Auth is not configured. Set <code>VITE_NEON_AUTH_URL</code> to enable accounts.
      </p>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={compact ? 'flex flex-col gap-3' : 'panel flex flex-col gap-4 p-5'}
    >
      {!compact ? (
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-400">
            {mode === 'signin' ? 'Sign in' : 'Create account'}
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            Save tickers to your watch history. Predictions stay public — accounts only store your
            own bookmarks.
          </p>
        </div>
      ) : null}

      <label className="flex flex-col gap-1 text-xs text-slate-400">
        Email
        <input
          className="input"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="you@example.com"
        />
      </label>

      <label className="flex flex-col gap-1 text-xs text-slate-400">
        Password
        <input
          className="input"
          type="password"
          required
          minLength={6}
          autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder="At least 6 characters"
        />
      </label>

      {error ? (
        <p role="alert" className="rounded-lg border border-bear/40 bg-bear/10 px-3 py-2 text-xs text-bear">
          {error}
        </p>
      ) : null}

      {notice ? (
        <p
          role="status"
          className="rounded-lg border border-bull/40 bg-bull/10 px-3 py-2 text-xs text-bull"
        >
          {notice}
        </p>
      ) : null}

      <div className="flex items-center justify-between gap-3">
        <button className="btn-primary" type="submit" disabled={busy}>
          {busy ? 'Working…' : mode === 'signin' ? 'Sign in' : 'Sign up'}
        </button>
        <button
          type="button"
          className="text-xs text-slate-400 underline decoration-dotted hover:text-slate-200"
          onClick={() => {
            setMode(mode === 'signin' ? 'signup' : 'signin');
            setError(null);
            setNotice(null);
          }}
        >
          {mode === 'signin' ? 'Need an account?' : 'I already have an account'}
        </button>
      </div>
    </form>
  );
}
